import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Card,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  AlertTitle,
  IconButton,
  Chip,
  alpha
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

const EnrollDialog = React.memo(({ 
  open, 
  onClose, 
  selectedCourse,
  selectedDepartment,
  selectedUser,
  deadlineDate,
  isAnnual,
  departments,
  usersInDepartment,
  onDepartmentChange,
  onUserChange,
  onDateChange,
  onAnnualChange,
  onEnroll
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      TransitionProps={{
        mountOnEnter: true,
        unmountOnExit: true
      }}
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' }
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PersonAddIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Inscribir Usuario
            </Typography>
            {selectedCourse && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {selectedCourse.title}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          {/* Departamento */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BusinessIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark }}>
                Departamento
              </Typography>
            </Box>
            <FormControl fullWidth>
              <Select
                value={selectedDepartment}
                onChange={onDepartmentChange}
                displayEmpty
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: COLORS.primary 
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: COLORS.primary 
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em>Selecciona un departamento</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Selecciona un departamento para filtrar usuarios
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Usuario */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark }}>
                Usuario
              </Typography>
            </Box>
            <FormControl fullWidth>
              <Select
                value={selectedUser}
                onChange={onUserChange}
                disabled={!selectedDepartment}
                displayEmpty
                sx={{ 
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 2
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': { 
                    borderColor: COLORS.primary 
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                    borderColor: COLORS.primary 
                  }
                }}
              >
                <MenuItem value="" disabled>
                  <em>Selecciona un usuario</em>
                </MenuItem>
                {usersInDepartment.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Puedes seleccionar un usuario específico o todos los del departamento
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Fecha límite */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EventIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.dark }}>
                Fecha Límite
              </Typography>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={deadlineDate}
                onChange={onDateChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth
                    helperText="Fecha límite para completar el curso"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': { borderWidth: 2 },
                        '&:hover fieldset': { borderColor: COLORS.primary },
                        '&.Mui-focused fieldset': { borderColor: COLORS.primary }
                      }
                    }}
                  />
                )}
                minDate={new Date()}
                disabled={!selectedUser}
              />
            </LocalizationProvider>
          </Grid>

          {/* Curso anual */}
          <Grid item xs={12}>
            <Card 
              variant="outlined" 
              sx={{ 
                p: 2,
                borderRadius: 2,
                border: `2px solid ${isAnnual ? alpha(COLORS.purple, 0.3) : '#e0e0e0'}`,
                bgcolor: isAnnual ? alpha(COLORS.purple, 0.05) : 'transparent',
                transition: 'all 0.3s ease'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isAnnual}
                    onChange={onAnnualChange}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: COLORS.purple },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.purple }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RepeatIcon sx={{ color: isAnnual ? COLORS.purple : '#9e9e9e' }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: isAnnual ? COLORS.purple : COLORS.dark }}>
                        Curso Anual Recurrente
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#636e72' }}>
                        El curso se repetirá automáticamente cada año
                      </Typography>
                    </Box>
                  </Box>
                }
              />

              {isAnnual && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 2,
                    borderRadius: 2,
                    bgcolor: alpha(COLORS.purple, 0.08),
                    border: `1px solid ${alpha(COLORS.purple, 0.2)}`,
                    '& .MuiAlert-icon': { color: COLORS.purple }
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 700 }}>Curso Recurrente Anual</AlertTitle>
                  Este curso se programará automáticamente cada año:
                  <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2 }}>
                    <li>Los usuarios seleccionados actualmente</li>
                    <li>Misma fecha límite ajustada al siguiente año</li>
                    <li>Independiente de si el curso se completa o no</li>
                  </Box>
                </Alert>
              )}
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={onClose}
          sx={{
            color: '#636e72',
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onEnroll}
          disabled={!selectedUser || !deadlineDate}
          startIcon={<PersonAddIcon />}
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            boxShadow: `0 4px 14px ${alpha(COLORS.gradient1, 0.4)}`,
            '&:hover': {
              boxShadow: `0 6px 20px ${alpha(COLORS.gradient1, 0.5)}`
            },
            '&.Mui-disabled': {
              background: '#e0e0e0'
            }
          }}
        >
          Inscribir
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default EnrollDialog;